import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { REMOVE_INTEGRATION } from '../../queries'
import { useMutation } from '@apollo/client'
import {
  LightspeedBusiness,
  LightspeedStore,
  getConnectedLightspeedStores
} from './types'
import { StepStatus } from 'hooks/useStep'
import BusinessSection from './BusinessSection'
import LocationSection from './LocationSection'
import CommonHeader from '../Common/CommonHeader'
import { ManageRoot } from '../styles'
import ConnectSection from './ConnectSection'
import LoyaltySection from './LoyaltySection'
import { PartnerName, PARTNER_SLUGS, isPartner } from '../../constants'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import { useIntegration } from '../../hooks/useIntegration'

const LIGHTSPEED_PATH = `/integrations/${PARTNER_SLUGS.LIGHTSPEED}`

const ManageLightspeed = () => {
  const navigate = useNavigate()
  const { addMessage } = useMessageStore()

  const { integrations, refetchIntegrations } = useMerchant()
  const {
    lightspeedBusinesses: businesses,
    lightspeedAuthExpired,
    lightspeedStores,
    lightspeedStoresLoading,
    refetchLightspeedStores
  } = useIntegration()

  const [hasIntegrationConnected, setHasIntegrationConnected] = useState(false)
  const [selectedBusiness, setSelectedBusiness] =
    useState<LightspeedBusiness | null>(null)

  const [currentStep, setCurrentStep] = useState<number>(1)

  const [removeIntegration] = useMutation(REMOVE_INTEGRATION)

  const connectedLocations = useMemo(
    () => getConnectedLightspeedStores(lightspeedStores),
    [lightspeedStores]
  )

  useEffect(() => {
    setHasIntegrationConnected(
      integrations?.some((partner: { partner: { name: string } }) =>
        isPartner(partner.partner.name, PartnerName.LIGHTSPEED)
      )
    )
  }, [integrations])

  useEffect(() => {
    if (!connectedLocations.length || !businesses.length) {
      return
    }

    const business = businesses.find(
      (business) =>
        business.id ===
        connectedLocations[0].store_integration_settings.lightspeed_business_id
    )

    if (business) {
      setSelectedBusiness(business)
      setCurrentStep(2)
    }
  }, [connectedLocations, businesses])

  const handleBusinessFinish = (business: LightspeedBusiness | null) => {
    if (business) {
      setCurrentStep(2)
    }

    setSelectedBusiness(business)
  }

  const handleDisconnectBusiness = () => {
    setSelectedBusiness(null)
    setCurrentStep(1)
  }

  const handleSelectLocation = (location: LightspeedStore) => {
    navigate(`${LIGHTSPEED_PATH}/store/${location.id}`)
  }

  const handleConnectStore = () => {
    navigate(
      `${LIGHTSPEED_PATH}/store/connect?businessId=${selectedBusiness?.id}`
    )
  }

  const handleBulkDisconnect = () => {
    refetchLightspeedStores()
  }

  const handleBulkSyncMenu = async () => {
    await refetchLightspeedStores()
  }

  const handleRemoveIntegration = async () => {
    await removeIntegration({
      variables: { partnerName: PartnerName.LIGHTSPEED }
    })

    refetchIntegrations()

    addMessage({
      type: 'message',
      content: 'Integration removed successfully!'
    })
  }

  if (!hasIntegrationConnected || lightspeedAuthExpired) {
    return (
      <ManageRoot>
        <CommonHeader
          title='Connect Lightspeed'
          onBack={() => navigate('/integrations')}
          helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
        />
        <ConnectSection />
      </ManageRoot>
    )
  }

  return (
    <ManageRoot>
      <CommonHeader
        title='Connect Lightspeed'
        onBack={() => navigate('/integrations')}
        helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
      />
      <BusinessSection
        businesses={businesses}
        selectedBusiness={selectedBusiness}
        onFinish={handleBusinessFinish}
        stepStatus={
          currentStep === 1 ? StepStatus.active : StepStatus.completed
        }
        onDisconnect={handleDisconnectBusiness}
        canDisconnect={connectedLocations.length === 0}
        onRemoveIntegration={handleRemoveIntegration}
      />
      {currentStep > 1 && !!selectedBusiness && (
        <LocationSection
          connectedLocations={connectedLocations}
          onConnect={handleConnectStore}
          onSelect={handleSelectLocation}
          stepStatus={StepStatus.active}
          onBulkDisconnect={handleBulkDisconnect}
          onBulkSyncMenu={handleBulkSyncMenu}
          isLoading={lightspeedStoresLoading}
        />
      )}
      {currentStep > 1 && !!selectedBusiness && !!connectedLocations.length && (
        <LoyaltySection stepStatus={StepStatus.active} />
      )}
    </ManageRoot>
  )
}

export default ManageLightspeed
